import { getUserState } from '@carvana/core-auth';
import { get, isEmpty } from 'lodash';

import { getOriginContext } from './getOriginContext';
import { getUserInfoContext, getVehicleContext } from './getInfoContext';

import { getCookieItem, getLocalStorageItem } from 'utils/browserCache';
import { getUserIdentifier } from 'utils/id';
import { getIsNativeMobileApp } from 'utils/mobile';
import { COOKIE_KEY, LOCAL_STORAGE_KEY } from 'constants/browserCache';

import { MojoContextInfoType, MojoContextType, NativeAppPutContextType, UserType } from './types';
import { AuthenticationStateType } from 'hooks/authentication/types';
import { CustomerStateType, UserInfoType } from 'hooks/customer/types';
import { parseToken } from 'utils/token';

export const getChatContext = (): {
  authenticationState: Partial<AuthenticationStateType>;
  customerState: Partial<CustomerStateType>;
} => {
  const { browserCookieId, carvanaDeviceId, userIdentifierType } = getUserIdentifier();

  if (getIsNativeMobileApp()) {
    return iosMobileChatContext(browserCookieId, carvanaDeviceId, userIdentifierType);
  }

  return webChatContext(browserCookieId, carvanaDeviceId, userIdentifierType);
};

export const getMojoContext = (): MojoContextType => {
  const newContext = getChatContext();
  const cachedMojoContext = getLocalStorageItem(LOCAL_STORAGE_KEY.CVNA_PROPEL_CHAT_CONTEXT);

  const getInfoContext = (): MojoContextInfoType => {
    const userInfo = getUserInfoContext(newContext, cachedMojoContext);
    const vehicle = getVehicleContext(cachedMojoContext);
    return {
      user_info: {
        ...userInfo,
        geoip_state_abbreviation: getCookieItem('CVCurrentState'),
      },
      ...(vehicle && { vehicle }),
    };
  };

  return {
    core: getOriginContext(),
    info: getInfoContext(),
  };
};

/***
 * iOS native app has its own logic and views. This detail is especially important when navigating Search Results Page (SRP)
 * and Vehicle Details Page (VDP). Since iOS app user interaction is isolated from our ChatUI, we cannot listen to URL
 * for changes. iOS app will call our putContext upon loading certain views and we then update chatContext via API.
 *
 ** @param incomingContext Payload from iOS app for putContext call.
 * Data received is not 1:1 with our context schema. Realign with API via mappings.
 *
 */
export const mapMojoContextNativeMobileApp = (incomingContext: NativeAppPutContextType) => {
  const context: any = getMojoContext();

  let nativeAppContext = { ...incomingContext };
  try {
    // @ts-ignore // eslint-disable-next-line
    nativeAppContext = JSON.parse(nativeAppContext);
  } catch (e) {
    // FIXME - do nothing. Mobile app may call putContext with stringified data and/or object type. Should align and fix discrepancy.
  }
  if (nativeAppContext?.vendor_id) {
    context.core.origin.vendor_id = nativeAppContext.vendor_id;
  }
  if (nativeAppContext?.user_id) {
    context.info.user_info.user_id = nativeAppContext.user_id;
  }
  if (nativeAppContext?.carvana_device_id) {
    context.info.user_info.carvana_device_id = nativeAppContext.carvana_device_id;
  }
  if (nativeAppContext?.filters) {
    context.info.filters = nativeAppContext.filters;
  }
  if (nativeAppContext.vehicle) {
    context.info.vehicle = nativeAppContext.vehicle;
  }
  return context;
};

/**
 * On Web, user meta is retrieved from cookies and values only available from CORE auth
 * which depends on Header. More comprehensive. Hydrate store values.
 */
const webChatContext = (
  browserCookieId: string,
  carvanaDeviceId: string | null,
  userIdentifierType: string,
): {
  authenticationState: Partial<AuthenticationStateType>;
  customerState: Partial<CustomerStateType>;
} => {
  const Geolocation = getLocalStorageItem(LOCAL_STORAGE_KEY.GEOLOCATION);

  const { isLoggedIn, user } = getUserState();
  const { email, user_id, given_name, family_name }: UserType = user;

  const userInfo: Partial<UserInfoType> = {
    city: get(Geolocation, 'City', undefined),
    ipAddress: get(Geolocation, 'IPAddress', undefined),
    stateAbbreviation: get(Geolocation, 'State', undefined),
    zip5: get(Geolocation, 'Zip5', undefined),
    email: email,
    firstName: given_name,
    lastName: family_name,
  };

  return {
    authenticationState: {
      browserCookieId,
      carvanaDeviceId,
      user: isEmpty(user) ? null : user,
      userIdentifierType,
    },
    customerState: {
      isLoggedIn,
      userInfo,
    },
  };
};

/**
 * iOS native app only provides limited information compared to Web due to missing CORE integration.
 * The native iOS app passes a 'CVIdToken' cookie value through its own auth.
 */
export const iosMobileChatContext = (
  browserCookieId: string,
  carvanaDeviceId: string | null,
  userIdentifierType: string,
): {
  authenticationState: Partial<AuthenticationStateType>;
  customerState: Partial<CustomerStateType>;
} => {
  const idToken = getCookieItem(COOKIE_KEY.CVNA_ID_TOKEN);
  const parsedIdToken = parseToken(idToken);

  let userInfoAuthState: UserType | null = null;
  let userInfoCustomerState: Partial<UserInfoType> | null = null;
  if (parsedIdToken) {
    const { name, email, user_id, given_name, family_name, sub }: UserType = parsedIdToken;
    userInfoAuthState = {
      email,
      name,
      user_id,
      given_name,
      family_name,
      sub,
      // is_new_3p?: boolean;
    };
    userInfoCustomerState = {
      city: undefined,
      ipAddress: undefined,
      stateAbbreviation: undefined,
      zip5: undefined,
      email: email,
      firstName: given_name,
      lastName: family_name,
    };
  }

  return {
    authenticationState: {
      browserCookieId,
      carvanaDeviceId,
      user: userInfoAuthState,
      userIdentifierType,
    },
    customerState: {
      isLoggedIn: Boolean(idToken),
      userInfo: userInfoCustomerState,
    },
  };
};
